import { useMemo } from 'react';
import { Image } from 'react-datocms';
import { useIsMobileScreen, useIsTabletScreen } from '@carbonify/ui';
import clsx from 'clsx';
import CtaButton from 'components/CtaButton';
import InlineRichText from 'components/RichContent/InlineRichText';
import { ResponsiveImage } from 'services/graphql';

import useHeroTheme from './useHeroTheme';

import styles from './Hero.module.css';

interface HeroModuleRecord {
  variant: 'text_left' | 'text_center' | 'blog_landing' | 'blog_post' | '2_col' | '2_row';
  theme: 'auto' | 'dark' | 'light';
  /**
   * Markdown
   */
  title?: string;

  /**
   * Markdown
   */
  description?: string;

  /**
   * Markdown
   */
  ctaMain?: string;

  /**
   * Markdown
   */
  ctaSecondary?: string;

  imageDesktop?: ResponsiveImage;
  imageTablet?: ResponsiveImage;
  imageMobile?: ResponsiveImage;

  className?: string;

  mobileContentAlignement?: 'top' | 'middle' | 'bottom' | 'default';
  desktopContentAlignement?: 'middle_left' | 'middle_center' | 'bottom_left' | 'bottom_center' | 'default';
}

export default function Hero({
  title,
  description,
  ctaMain,
  ctaSecondary,
  variant = 'text_center',
  theme = 'auto',
  imageDesktop,
  imageMobile,
  imageTablet,
  className,
  mobileContentAlignement = 'default',
  desktopContentAlignement = 'default',
}: HeroModuleRecord) {
  const isTablet = useIsTabletScreen(false);
  const isMobile = useIsMobileScreen(false);
  const image = useMemo(() => {
    const map = [
      { image: imageMobile, condition: isMobile },
      { image: imageTablet, condition: isTablet },
      { image: imageDesktop, condition: true },
    ];

    const fallback = imageDesktop || imageTablet || imageMobile;

    return map.find(({ condition }) => condition)?.image?.responsiveImage || fallback?.responsiveImage;
  }, [isTablet, isMobile, imageDesktop, imageMobile, imageTablet]);

  const heroTheme = useHeroTheme({ theme, image, variant });

  return (
    <div
      data-testid={`hero-${variant}`}
      data-nav-theme={heroTheme}
      className={clsx(
        styles.hero,
        styles[variant],
        styles[heroTheme],
        styles[`desktop-${desktopContentAlignement}`],
        styles[`mobile-${mobileContentAlignement}`],
      )}
    >
      <div className={styles.wrapper}>
        <div className={clsx(styles.content, className)}>
          {title && (
            <InlineRichText
              as="h1"
              className={styles.title}
            >
              {title}
            </InlineRichText>
          )}
          {description && (
            <InlineRichText
              as="p"
              className={styles.subtitle}
            >
              {description}
            </InlineRichText>
          )}
          <div className={styles.buttons}>
            {ctaMain && <CtaButton value={ctaMain} />}
            {ctaSecondary && (
              <CtaButton
                variant="ghost"
                highlight={heroTheme === 'dark' || !variant.includes('2_c')}
                value={ctaSecondary}
              />
            )}
          </div>
        </div>
      </div>
      {image && (
        <div className={styles.background}>
          {/* eslint-disable-next-line jsx-a11y/alt-text */}
          <Image
            data={image}
            layout="fill"
            objectFit="cover"
            objectPosition="50% 50%"
          />
        </div>
      )}
    </div>
  );
}
